import React, { useEffect, useId, useState } from 'react';
import { isEmpty, isNil, sortBy } from 'lodash';

import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import { CONVERSATION_TEXTS } from '../../conversation/texts';
import type { QueuedMessage, SendingMessage } from '../../conversation/types';
import { formatEntryTime, prefersReducedMotion } from '../../utils';

/** How long the strip takes to fold away once its last message has gone into the log. */
const COLLAPSE_MS = 450;

/** A row of the strip: the message's id, when it was sent and its text. */
export type QueueRow = Pick<QueuedMessage, 'id' | 'created_at' | 'text'>;

/** A row's box: its time, then its text, against a rule down its left. */
const ROW_CLASSES =
  'grid grid-cols-[56px_minmax(0,1fr)] items-baseline gap-8 border-0 border-l-2 border-solid py-4 pl-12';

interface QueueStripProps {
  /** The messages no run has read yet. */
  messages: QueueRow[];
  /**
   * The handler's messages the server has but the queue has not listed yet, in the order they were sent: drawn
   * after the listed ones, as their times are the browser's and not the server's.
   */
  sent?: QueueRow[];
  /** The message on its way to Clive, drawn last and greyed until the server answers. */
  sending?: SendingMessage | null;
  /** Hands each row's element over, by message id, for the dissolve to see it into the log from. */
  itemRef: (messageId: string) => (element: HTMLElement | null) => void;
}

/**
 * The messages Clive has not read yet, together, between the log and the composer. They are not turns of
 * the conversation until a run reads them, so no bubble and no avatar: the time each was sent, then its
 * text, oldest first. A message on its way to Clive comes last, in slate rather than azure, until the server
 * has it. Hidden while empty; when its last message goes into the log, or the one on its way did not go, the strip
 * folds away rather than vanishing, so the log moves down instead of jumping.
 */
const QueueStrip: React.FC<QueueStripProps> = ({ messages, sent = [], sending, itemRef }) => {
  const headerId = useId();
  const isOpen = !isEmpty(messages) || !isEmpty(sent) || !isNil(sending);
  // Drawn while open and for the fold after; an empty strip that was never open draws nothing.
  const [isDrawn, setIsDrawn] = useState(isOpen);
  if (isOpen && !isDrawn) setIsDrawn(true);

  useEffect(() => {
    if (isOpen || !isDrawn) return undefined;

    const timer = window.setTimeout(() => setIsDrawn(false), prefersReducedMotion() ? 0 : COLLAPSE_MS);

    return () => window.clearTimeout(timer);
  }, [isOpen, isDrawn]);

  if (!isDrawn) return null;

  return (
    <div
      aria-hidden={!isOpen}
      className={cn(
        'grid transition-[grid-template-rows] duration-[450ms] ease-out motion-reduce:transition-none',
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <section aria-labelledby={headerId} className="min-h-0 overflow-hidden">
        <div className="mt-8 border-0 border-t border-solid border-slate-200 pb-4 pt-4">
          <div id={headerId} className="pb-4">
            <Text
              variant={Text.VARIANTS.XXS}
              weight={Text.WEIGHTS.SEMI_BOLD}
              className="inline uppercase tracking-wide text-azure-700"
            >
              {CONVERSATION_TEXTS.QUEUE_HEADER}
            </Text>
          </div>
          <ol className="m-0 flex list-none flex-col gap-4 p-0">
            {[...sortBy(messages, ({ created_at: createdAt }) => Date.parse(createdAt)), ...sent].map(
              ({ id, created_at: createdAt, text }) => (
                <li key={id} ref={itemRef(id)} className={cn(ROW_CLASSES, 'border-azure-700')}>
                  <Text
                    variant={Text.VARIANTS.XS}
                    colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                    className="whitespace-nowrap"
                  >
                    {formatEntryTime(createdAt)}
                  </Text>
                  <Text variant={Text.VARIANTS.SM} className="text-slate-900 wrap-anywhere">
                    {text}
                  </Text>
                </li>
              )
            )}
            {sending ? (
              // Positioned, so its hidden word is placed in the row and clipped with the strip as it folds.
              <li aria-busy="true" className={cn(ROW_CLASSES, 'relative border-slate-700')}>
                <span className="sr-only">{CONVERSATION_TEXTS.SENDING_ROW}</span>
                <Text
                  variant={Text.VARIANTS.XS}
                  colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                  className="whitespace-nowrap"
                >
                  {formatEntryTime(sending.sentAt)}
                </Text>
                <Text variant={Text.VARIANTS.SM} className="text-slate-800 wrap-anywhere">
                  {sending.text}
                </Text>
              </li>
            ) : null}
          </ol>
        </div>
      </section>
    </div>
  );
};

export default QueueStrip;
